/**
 * Payment Configuration API Contracts (#773 / #781)
 *
 * The provider registry, the method catalogue and the routing table that let a
 * Super Admin enable, disable, reorder and reroute payment methods without a
 * deploy. Served by `payment-service`; consumed by `apps/admin`.
 *
 * Every vocabulary below DERIVES from `@autopia/shared-types` and none is
 * re-listed here (AGENTS.md Rule #7c). The design is
 * `docs/design/features/payment-configuration.md`; section references in the
 * comments point there unless another document is named.
 *
 * ## The one rule that is enforced rather than documented
 *
 * No payment configuration table, and no schema in this file, may carry a
 * gateway credential — not a secret, not a path to one, not the name of a key.
 * `payment-service` owns the catalogue and the routing; `config-service` keeps
 * secrets; the credential itself reaches an adapter as an environment variable
 * and never travels through a row an admin can read (§4.10, ADR-036 §3a).
 *
 * A column-name check alone would not hold that line, because both configurable
 * tables carry a free-form `config Json` that the admin API makes writable.
 * Nothing about a column list stops `PATCH /v1/admin/payment-providers/:id`
 * from writing `{"serverKey": "SB-Mid-server-…"}` into it, which would land a
 * live gateway secret in an admin-readable table, in database backups and in
 * replica streams — with every test still green. So `config` on both tables is a
 * CLOSED schema here (§5.1 invariant 6, clause B): `.strict()`, known keys only,
 * and any credential-shaped key rejected by name with a message that says why.
 */

import {
  CHECKOUT_GROUPS,
  CHECKOUT_METHODS,
  CONCRETE_SALES_CHANNELS,
  PAYMENT_FEE_TYPES,
  PAYMENT_PROVIDER_TYPES,
  SALES_CHANNELS,
} from '@autopia/shared-types';
import { z } from 'zod';

// ============================================================================
// Vocabularies — all derived, none re-listed
// ============================================================================

/*
 * `CHECKOUT_METHODS` and `PAYMENT_FEE_TYPES` are used as `z.enum(...)` inline
 * below rather than by importing `CheckoutMethodSchema` and
 * `PaymentFeeTypeSchema` from `./index`. That import would be circular —
 * `./index` re-exports this file — and a cycle that happens to work because of
 * declaration order is a cycle that breaks when someone reorders the file.
 *
 * Nothing is duplicated by this: both call sites derive from the same single
 * declaration in `@autopia/shared-types`, so they cannot disagree. What Rule #7c
 * forbids is a second *list*, not a second `z.enum` over the same list.
 */

/** Derived from `PAYMENT_PROVIDER_TYPES`. Level 1 of the epic's vocabulary. */
export const PaymentProviderTypeSchema = z.enum(PAYMENT_PROVIDER_TYPES);

export type PaymentProviderType = z.infer<typeof PaymentProviderTypeSchema>;

/**
 * Derived from `SALES_CHANNELS`, including the `ALL` wildcard. Correct for a
 * configuration row, which may be scoped to every channel at once.
 */
export const SalesChannelSchema = z.enum(SALES_CHANNELS);

export type SalesChannel = z.infer<typeof SalesChannelSchema>;

/**
 * Derived from `CONCRETE_SALES_CHANNELS` — every channel except `ALL`.
 *
 * A real sale happens on exactly one channel, so a *request* names one of these.
 * `ALL` describes the scope of a configuration row, not the origin of a
 * purchase, and accepting it on the public availability endpoint would quietly
 * return the union of every channel's methods rather than an error.
 */
export const ConcreteSalesChannelSchema = z.enum(CONCRETE_SALES_CHANNELS);

export type ConcreteSalesChannel = z.infer<typeof ConcreteSalesChannelSchema>;

/** Derived from `CHECKOUT_GROUPS`. The picker header a catalogue row sits under. */
export const CheckoutGroupSchema = z.enum(CHECKOUT_GROUPS);

export type CheckoutGroup = z.infer<typeof CheckoutGroupSchema>;

/**
 * A provider's URL-safe key: `midtrans`, `edc-permata`. Lowercase alphanumerics
 * and single hyphens.
 *
 * The admin API derives each row's `configPath` from this, so it lands in a URL
 * (§7.1). It is constrained rather than left as a free string for that reason —
 * a slug carrying a slash or a `..` would be a path, and storing a path is what
 * §7.1 exists to prevent. **Never store a URL here.**
 */
export const ProviderSlugSchema = z
  .string()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be lowercase alphanumerics separated by single hyphens');

// ============================================================================
// `config` payloads — closed, per §5.1 invariant 6 clause B
// ============================================================================

/**
 * Keys that may never appear in a configuration payload, at any depth.
 *
 * Deliberately a substring match rather than an exact list: the point is to
 * catch `serverKey`, `midtrans_server_key`, `callbackToken` and
 * `webhookSecret` alike, including ones nobody has thought of yet. Modelled on
 * the monolith's `scripts/check-catalog-env-parity.js:73-77`, which fails the
 * build with `STAY_SECRET_IN_CATALOG`.
 */
export const CREDENTIAL_SHAPED_KEY = /key|secret|token|credential|password|passphrase|private/i;

/**
 * Rejects any credential-shaped key anywhere in a payload, at any depth.
 *
 * `.strict()` already rejects unknown keys, so on the schemas below this is
 * strictly redundant today — and that is the point. It fires with a message
 * naming the offending key and the rule, rather than the generic
 * "unrecognized_keys", so an operator who pastes a server key into the config
 * box is told what is wrong instead of guessing. It also keeps holding the line
 * if a future known key is ever an object with free-form contents.
 */
function rejectCredentialShapedKeys(value: unknown, ctx: z.RefinementCtx, path: string[] = []): void {
  if (value === null || typeof value !== 'object') return;

  if (Array.isArray(value)) {
    value.forEach((entry, index) => rejectCredentialShapedKeys(entry, ctx, [...path, String(index)]));
    return;
  }

  for (const [key, nested] of Object.entries(value)) {
    if (CREDENTIAL_SHAPED_KEY.test(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...path, key],
        message:
          `"${key}" is credential-shaped and payment configuration must never hold a secret. ` +
          'Gateway credentials reach their adapter as environment variables; config-service keeps ' +
          'secrets. See payment-configuration.md §4.10.',
      });
      continue;
    }
    rejectCredentialShapedKeys(nested, ctx, [...path, key]);
  }
}

/**
 * Runs the credential check BEFORE the closed-object check, so the operator gets
 * the message that explains the rule rather than the one that reads like a typo.
 *
 * The ordering has to be structural, not conventional. A `.superRefine()` hung
 * off a `.strict()` object never runs when the object already failed — zod skips
 * refinements on an invalid base — so `{"serverKey": "…"}` would report
 * `unrecognized_keys` and nothing else. That message invites the operator to try
 * a different name for the same secret. Piping puts the guard upstream, where it
 * sees every payload including the ones `.strict()` would reject.
 */
function withCredentialGuard<T extends z.ZodTypeAny>(schema: T) {
  return z
    .unknown()
    .superRefine((value, ctx) => rejectCredentialShapedKeys(value, ctx))
    .pipe(schema);
}

/**
 * A card BIN restriction entry, as Midtrans accepts it for
 * `credit_card.whitelist_bins` (§6.2): the first 1–8 digits of a card number, or
 * an Indonesian issuer code.
 */
export const CardBinRestrictionSchema = z.union([
  z.string().regex(/^\d{1,8}$/, 'must be 1-8 leading digits of a card number'),
  z.enum(['bca', 'bni', 'bri', 'cimb', 'mandiri', 'permata']),
]);

export type CardBinRestriction = z.infer<typeof CardBinRestrictionSchema>;

/**
 * The behaviour knobs a catalogue row may carry. Closed by `.strict()`.
 *
 * Adding a key here is a contract change under AGENTS.md Rule #2 — schema,
 * controller and every consumer in one PR. That friction is intentional: an
 * open payload is how a secret gets in.
 */
export const PaymentMethodConfigSchema = withCredentialGuard(
  z
    .object({
      /** Flows through to Midtrans `credit_card.whitelist_bins` (§6.2). */
      allowedCardBins: z.array(CardBinRestrictionSchema).max(64).optional(),
      /** Per-channel QR expiry, retained from ADR-023 whose adapter design stands. */
      qrExpiryMinutes: z.number().int().min(1).max(1440).optional(),
    })
    .strict(),
);

export type PaymentMethodConfig = z.infer<typeof PaymentMethodConfigSchema>;

/**
 * The behaviour knobs a provider row may carry. Closed by `.strict()`, and
 * currently **empty** — no provider-level knob is defined yet.
 *
 * An empty closed object is a deliberate answer, not an oversight. The
 * `payment_gateways.config` column has existed since February 2026 and has never
 * held a row, so there is no shape to preserve; the only proposals for it so far
 * have been credential references, which §4.10 rejected outright. Starting
 * closed means the first genuine knob arrives through a reviewed contract change
 * rather than through whatever an operator happened to paste.
 */
export const PaymentProviderConfigSchema = withCredentialGuard(z.object({}).strict());

export type PaymentProviderConfig = z.infer<typeof PaymentProviderConfigSchema>;

// ============================================================================
// Query parameters
// ============================================================================

/**
 * A boolean supplied as a query string.
 *
 * Explicitly **not** `z.coerce.boolean()`, which is `Boolean(value)` and so
 * parses the string `'false'` as `true` — every filter written that way silently
 * ignores the one value an operator is most likely to type. `?isActive=false`
 * has to mean false.
 */
export const BooleanQueryParamSchema = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((value) => value === true || value === 'true' || value === '1');

// ============================================================================
// Amounts
// ============================================================================

/**
 * An IDR amount bound on a catalogue row.
 *
 * The columns are `BigInt?`, which JSON cannot carry and `Number` can only hold
 * exactly up to 2^53-1. Bounded here rather than left as a bare `z.number()` so
 * the boundary rejects a value that would silently lose precision on the way
 * through, instead of the frontend rendering a wrong maximum (§6.1 item 5).
 * Nullable, not optional: a row with no upper bound says so.
 */
export const PaymentAmountBoundSchema = z
  .number()
  .int()
  .nonnegative()
  .max(Number.MAX_SAFE_INTEGER);

// ============================================================================
// Providers — level 2
// ============================================================================

/**
 * One row of the provider registry. Response shape of
 * `GET /v1/admin/payment-providers`.
 *
 * Carries **no credential field of any kind**, by the rule at the head of this
 * file. It also carries no `configPath` in the database — that is computed from
 * `slug` on the way out (§7.1), and is `null` when no admin page exists for the
 * slug so the UI renders plain text rather than a link to a 404.
 */
export const PaymentProviderSchema = z.object({
  id: z.string(),
  /** URL-safe key. Derives `configPath`; never a URL. */
  slug: ProviderSlugSchema,
  /** Operator-facing identifier, e.g. `Midtrans`, `EDC Permata` (§9.3). */
  name: z.string().min(1).max(120),
  /**
   * The reconciliation `GatewayType` discriminator the nightly job reads
   * (§9.3). Distinct from `slug`, which is the URL key, and from `name`, which
   * is the label. Collapsing the three is out of scope; leaving it unstated is
   * how the seed diverges.
   */
  provider: z.string().min(1).max(64),
  providerType: PaymentProviderTypeSchema,
  isActive: z.boolean(),
  isDefault: z.boolean(),
  /** Order within a provider type on the admin hub. Lower sorts first. */
  priority: z.number().int(),
  /**
   * The methods this provider's adapter declares it can process. Mirrors the
   * adapter's `supportedMethods`, and a route may not name a method absent from
   * it (§5.1 invariant 3).
   */
  supportedMethods: z.array(z.enum(CHECKOUT_METHODS)),
  config: PaymentProviderConfigSchema,
  healthStatus: z.string().nullable(),
  lastHealthCheckAt: z.string().datetime().nullable(),
  /** Computed from `slug`; `null` when no admin page serves it (§7.1). */
  configPath: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PaymentProvider = z.infer<typeof PaymentProviderSchema>;

export const ListPaymentProvidersQuerySchema = z.object({
  providerType: PaymentProviderTypeSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
});

export type ListPaymentProvidersQuery = z.infer<typeof ListPaymentProvidersQuerySchema>;

export const ListPaymentProvidersResponseSchema = z.object({
  providers: z.array(PaymentProviderSchema),
});

export type ListPaymentProvidersResponse = z.infer<typeof ListPaymentProvidersResponseSchema>;

/**
 * `POST /v1/admin/payment-providers`.
 *
 * Note what this does **not** promise. A provider with a charge path also needs
 * a `PaymentGatewayProvider` enum member and a registered adapter, both of which
 * are code — `payment-gateway.ts:40` throws on an unrecognised adapter name, and
 * that throw is deliberate. Creating a row here onboards a provider for
 * configuration, reporting and routing; it does not conjure an integration. The
 * epic's deploy-free promise is enable, disable, reorder and reroute.
 */
export const CreatePaymentProviderRequestSchema = z
  .object({
    slug: ProviderSlugSchema,
    name: z.string().min(1).max(120),
    provider: z.string().min(1).max(64),
    providerType: PaymentProviderTypeSchema,
    supportedMethods: z.array(z.enum(CHECKOUT_METHODS)),
    priority: z.number().int().min(0).default(0),
    isActive: z.boolean().default(false),
    isDefault: z.boolean().default(false),
    config: PaymentProviderConfigSchema.default({}),
  })
  .strict();

export type CreatePaymentProviderRequest = z.infer<typeof CreatePaymentProviderRequestSchema>;

/**
 * `PATCH /v1/admin/payment-providers/:id`.
 *
 * `slug` and `providerType` are absent on purpose: the slug is in URLs the admin
 * app links to and the provider type decides which page configures the row, so
 * both are identity rather than settings. Changing either is a delete and a
 * recreate, which is visible in the audit trail as one.
 */
export const UpdatePaymentProviderRequestSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    provider: z.string().min(1).max(64).optional(),
    supportedMethods: z.array(z.enum(CHECKOUT_METHODS)).optional(),
    priority: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
    isDefault: z.boolean().optional(),
    config: PaymentProviderConfigSchema.optional(),
  })
  .strict()
  .refine((body) => Object.keys(body).length > 0, {
    message: 'at least one field must be supplied',
  });

export type UpdatePaymentProviderRequest = z.infer<typeof UpdatePaymentProviderRequestSchema>;

// ============================================================================
// Routes — the ordered provider candidates for a method
// ============================================================================

/**
 * One edge of the routing table: this method, on this channel, tries this
 * provider at this position.
 *
 * `priority` starts at 1 and is contiguous within a `(method, channel)` pair
 * (§5.1 invariant 5), so the failover order is total and has no ties. The
 * provider's `slug` travels alongside its `id` because every consumer of a route
 * — the admin list, the resolver's log line, the audit event — identifies a
 * provider by slug, and re-fetching the registry to turn an id into one is a
 * join the caller should not have to do.
 */
export const PaymentMethodRouteSchema = z.object({
  id: z.string(),
  paymentMethodId: z.string(),
  providerId: z.string(),
  providerSlug: ProviderSlugSchema,
  salesChannel: SalesChannelSchema,
  priority: z.number().int().min(1),
  isEnabled: z.boolean(),
});

export type PaymentMethodRoute = z.infer<typeof PaymentMethodRouteSchema>;

/** One entry of the ordered list sent to `PUT /v1/admin/payment-methods/:id/routes`. */
export const PaymentMethodRouteInputSchema = z
  .object({
    providerId: z.string().min(1),
    salesChannel: SalesChannelSchema.default('ALL'),
    isEnabled: z.boolean().default(true),
  })
  .strict();

export type PaymentMethodRouteInput = z.infer<typeof PaymentMethodRouteInputSchema>;

/**
 * `PUT /v1/admin/payment-methods/:id/routes` — replaces the whole ordered list
 * atomically.
 *
 * **Order in the array is the failover order**, and there is no `priority` field
 * on the input for that reason. A client that sends both would be stating the
 * same fact twice and the server would have to decide which to believe on a
 * disagreement; array position cannot disagree with itself. The server assigns
 * `priority` 1..n from the array, which is also how invariant 5's contiguity is
 * satisfied without a compaction pass.
 *
 * PUT rather than PATCH because reordering is not expressible as a partial
 * update: moving Xendit above Midtrans changes two rows, and a client that sent
 * only the moved one would leave a duplicate priority behind.
 */
export const ReplacePaymentMethodRoutesRequestSchema = z
  .object({
    routes: z.array(PaymentMethodRouteInputSchema).max(32),
  })
  .strict();

export type ReplacePaymentMethodRoutesRequest = z.infer<
  typeof ReplacePaymentMethodRoutesRequestSchema
>;

export const PaymentMethodRoutesResponseSchema = z.object({
  routes: z.array(PaymentMethodRouteSchema),
});

export type PaymentMethodRoutesResponse = z.infer<typeof PaymentMethodRoutesResponseSchema>;

// ============================================================================
// The catalogue — level 3
// ============================================================================

/**
 * One row of the method catalogue: a payment option the park accepts, with its
 * fee, ordering, channel scope and ordered provider candidates.
 *
 * Keyed on the COARSE checkout vocabulary — what a customer selects — and not on
 * `PAYMENT_METHOD_CODES`, which is what a gateway webhook resolves afterwards
 * (§4.6). The two are not in bijection and keying on the granular list would
 * make `resolve()` uncallable from the charge path, because the granular code is
 * not knowable until the webhook arrives.
 *
 * Carries **no display name**. Labels resolve client-side from the canonical
 * enum through each app's own i18n store (§8), so an operator cannot rename a
 * method without a deploy — which is accepted, because adding one already needs
 * one.
 */
export const PaymentMethodCatalogSchema = z.object({
  id: z.string(),
  code: z.enum(CHECKOUT_METHODS),
  checkoutGroup: CheckoutGroupSchema,
  /** Order in the customer picker. Not the provider order — see `PaymentMethodRoute.priority`. */
  displayOrder: z.number().int(),
  isEnabled: z.boolean(),
  /** Where this method is offered. May contain `ALL`. */
  salesChannels: z.array(SalesChannelSchema),
  minAmountIdr: PaymentAmountBoundSchema.nullable(),
  maxAmountIdr: PaymentAmountBoundSchema.nullable(),
  feeType: z.enum(PAYMENT_FEE_TYPES),
  feeValue: z.number().nonnegative(),
  /** Operator-entered settlement estimate, e.g. `T+1`. Free text by design. */
  settlementEta: z.string().max(32).nullable(),
  config: PaymentMethodConfigSchema,
  routes: z.array(PaymentMethodRouteSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type PaymentMethodCatalog = z.infer<typeof PaymentMethodCatalogSchema>;

export const ListPaymentMethodsQuerySchema = z.object({
  salesChannel: SalesChannelSchema.optional(),
  isEnabled: BooleanQueryParamSchema.optional(),
  /** Soft-deleted rows are excluded unless this is set (§4.7). */
  includeDeleted: BooleanQueryParamSchema.optional(),
});

export type ListPaymentMethodsQuery = z.infer<typeof ListPaymentMethodsQuerySchema>;

export const ListPaymentMethodsResponseSchema = z.object({
  methods: z.array(PaymentMethodCatalogSchema),
});

export type ListPaymentMethodsResponse = z.infer<typeof ListPaymentMethodsResponseSchema>;

const paymentMethodWritableShape = {
  checkoutGroup: CheckoutGroupSchema,
  displayOrder: z.number().int().min(0),
  isEnabled: z.boolean(),
  salesChannels: z.array(SalesChannelSchema).min(1),
  minAmountIdr: PaymentAmountBoundSchema.nullable(),
  maxAmountIdr: PaymentAmountBoundSchema.nullable(),
  feeType: z.enum(PAYMENT_FEE_TYPES),
  feeValue: z.number().nonnegative(),
  settlementEta: z.string().max(32).nullable(),
  config: PaymentMethodConfigSchema,
};

/**
 * `POST /v1/admin/payment-methods`.
 *
 * `code` is only settable here. It is the identity of the row and is what every
 * route, every charge and every adapter keys on, so changing it on an existing
 * row would silently re-point live routing at a different method.
 */
export const CreatePaymentMethodRequestSchema = z
  .object({
    code: z.enum(CHECKOUT_METHODS),
    ...paymentMethodWritableShape,
    displayOrder: paymentMethodWritableShape.displayOrder.default(0),
    isEnabled: paymentMethodWritableShape.isEnabled.default(false),
    minAmountIdr: paymentMethodWritableShape.minAmountIdr.default(null),
    maxAmountIdr: paymentMethodWritableShape.maxAmountIdr.default(null),
    feeType: paymentMethodWritableShape.feeType.default('NONE'),
    feeValue: paymentMethodWritableShape.feeValue.default(0),
    settlementEta: paymentMethodWritableShape.settlementEta.default(null),
    config: paymentMethodWritableShape.config.default({}),
  })
  .strict();

export type CreatePaymentMethodRequest = z.infer<typeof CreatePaymentMethodRequestSchema>;

/** `PATCH /v1/admin/payment-methods/:id`. `code` is immutable — see the create schema. */
export const UpdatePaymentMethodRequestSchema = z
  .object({
    checkoutGroup: paymentMethodWritableShape.checkoutGroup.optional(),
    displayOrder: paymentMethodWritableShape.displayOrder.optional(),
    isEnabled: paymentMethodWritableShape.isEnabled.optional(),
    salesChannels: paymentMethodWritableShape.salesChannels.optional(),
    minAmountIdr: paymentMethodWritableShape.minAmountIdr.optional(),
    maxAmountIdr: paymentMethodWritableShape.maxAmountIdr.optional(),
    feeType: paymentMethodWritableShape.feeType.optional(),
    feeValue: paymentMethodWritableShape.feeValue.optional(),
    settlementEta: paymentMethodWritableShape.settlementEta.optional(),
    config: paymentMethodWritableShape.config.optional(),
  })
  .strict()
  .refine((body) => Object.keys(body).length > 0, {
    message: 'at least one field must be supplied',
  });

export type UpdatePaymentMethodRequest = z.infer<typeof UpdatePaymentMethodRequestSchema>;

// ============================================================================
// The configuration hub
// ============================================================================

/**
 * One tile on `/payments/configurations`: a provider type, how many providers it
 * holds and how many of those are active.
 *
 * `enabledCount` is served rather than left to the client to derive, because the
 * hub lists types whose providers it does not fetch — the tile says "3 of 3
 * active" without the page holding three provider rows.
 */
export const PaymentConfigurationSummarySchema = z.object({
  providerType: PaymentProviderTypeSchema,
  providerCount: z.number().int().nonnegative(),
  enabledCount: z.number().int().nonnegative(),
  /** `null` when no admin page serves this type yet — the UI renders plain text (§7.1). */
  configPath: z.string().nullable(),
});

export type PaymentConfigurationSummary = z.infer<typeof PaymentConfigurationSummarySchema>;

/** Response of `GET /v1/admin/payment-configurations`. */
export const GetPaymentConfigurationsResponseSchema = z.object({
  configurations: z.array(PaymentConfigurationSummarySchema),
});

export type GetPaymentConfigurationsResponse = z.infer<
  typeof GetPaymentConfigurationsResponseSchema
>;
